import { useEffect, useRef, useState } from 'react'

/**
 * Zählt eine Zahl beim Erscheinen von 0 auf ihren Wert hoch.
 *
 * Gestartet wird, sobald die Kachel zu sehen ist: steht sie beim Einhängen
 * schon im Bild, sofort — sonst über einen `IntersectionObserver`. Ohne das
 * liefe die Animation für Kacheln unterhalb des Bildrands ab, während niemand
 * hinsieht, und wer hinunterscrollt, fände fertige Zahlen vor.
 *
 * **`prefers-reduced-motion` schaltet es ab**: Dann steht der Wert sofort da.
 * Die Zahl ist der Inhalt, das Hochzählen nur die Art, sie zu zeigen.
 *
 * **Die Zahl muss ankommen, auch wenn die Animation ausbleibt.** Jeder
 * Effektlauf ist deshalb für sich abgeschlossen und trägt eine Notbremse: Ist
 * nach zwei Sekunden nichts gezählt worden, steht der Wert trotzdem da. Ein
 * Merker über Effektläufe hinweg hatte genau das verhindert — lief der Effekt
 * ein zweites Mal (neue Zahlen vom Server), hielt der Merker aus dem ersten
 * Lauf das Zählen an, und in den Kacheln blieb **0** stehen.
 */

const DAUER = 1100
const NOTBREMSE = 2000

// Schnell anfangen, sanft auslaufen: Ein gleichmäßiges Hochzählen wirkt wie
// eine Ladeanzeige, dieses hier wie ein Ergebnis.
const abbremsen = t => 1 - Math.pow(1 - t, 3)

export default function useCountUp(zielwert) {
	const ziel = Number(zielwert) || 0
	const elementRef = useRef(null)
	const [wert, setWert] = useState(0)

	useEffect(() => {
		if (!ziel) {
			setWert(0)
			return undefined
		}

		const bewegungAus = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches
		if (bewegungAus) {
			setWert(ziel)
			return undefined
		}

		let frame = null
		let notbremse = null
		let beobachter = null
		let fertig = false

		const zaehlen = () => {
			if (fertig) return
			fertig = true

			const beginn = performance.now()
			const schritt = jetzt => {
				const anteil = Math.min(1, (jetzt - beginn) / DAUER)
				setWert(Math.round(ziel * abbremsen(anteil)))
				if (anteil < 1) frame = window.requestAnimationFrame(schritt)
			}

			frame = window.requestAnimationFrame(schritt)
		}

		const el = elementRef.current
		const kasten = el?.getBoundingClientRect()
		const schonImBild = kasten && kasten.top < window.innerHeight && kasten.bottom > 0

		if (schonImBild || !el || typeof window.IntersectionObserver !== 'function') {
			zaehlen()
		} else {
			beobachter = new window.IntersectionObserver(
				eintraege => {
					if (eintraege.some(eintrag => eintrag.isIntersecting)) {
						beobachter.disconnect()
						zaehlen()
					}
				},
				{ threshold: 0.4 }
			)
			beobachter.observe(el)

			// Bleibt der Beobachter stumm, steht die Zahl trotzdem da.
			notbremse = window.setTimeout(() => {
				if (!fertig) {
					fertig = true
					setWert(ziel)
				}
			}, NOTBREMSE)
		}

		return () => {
			if (frame) window.cancelAnimationFrame(frame)
			if (notbremse) window.clearTimeout(notbremse)
			if (beobachter) beobachter.disconnect()
		}
	}, [ziel])

	return { elementRef, wert }
}
